import React, { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, ArrowLeft, Check, Loader2, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ==== Static options (from the Business TNA Survey) ==== */
const INDUSTRIES = [
  "Manufacturing",
  "IT/ITES",
  "Healthcare",
  "Education",
  "Hospitality",
  "Retail",
  "Pharma",
  "Real Estate",
  "Logistics",
  "Other",
];
const EMPLOYEE_BUCKETS = [
  "1–10",
  "11–25",
  "26–50",
  "51–100",
  "101–250",
  "250+",
];
const BUSINESS_CHALLENGES = [
  "Customer Service",
  "Communication Skills",
  "Sales Conversion",
  "Leadership Skills",
  "Teamwork",
  "Employee Motivation",
  "Time Management",
  "Workplace Discipline",
  "Conflict Management",
  "Employee Engagement",
  "Professional Etiquette",
  "Stress Management",
  "Manager Effectiveness",
  "First-Time Manager Development",
  "POSH Awareness",
  "Interview Skills",
  "Hiring Skills",
];
const PROGRAM_CATEGORIES = [
  {
    id: "leadership",
    name: "Leadership",
    programs: [
      "Leadership Development",
      "First-Time Manager Program",
      "Coaching & Feedback",
      "Delegation",
      "Decision Making",
      "Performance Management",
    ],
  },
  {
    id: "communication",
    name: "Communication",
    programs: [
      "Business Communication",
      "Professional Email Writing",
      "Presentation Skills",
      "Public Speaking",
      "Executive Presence",
      "Difficult Conversations",
    ],
  },
  {
    id: "customer-service",
    name: "Customer Service",
    programs: [
      "Customer Experience Excellence",
      "Complaint Handling",
      "Telephone Etiquette",
      "Client Relationship Management",
      "Service Recovery",
    ],
  },
  {
    id: "sales",
    name: "Sales",
    programs: [
      "Consultative Selling",
      "Negotiation Skills",
      "Cross-selling & Upselling",
      "Sales Communication",
    ],
  },
  {
    id: "workplace",
    name: "Workplace Skills",
    programs: [
      "Team Building",
      "Emotional Intelligence",
      "Time Management",
      "Productivity",
      "Problem Solving",
      "Workplace Ethics",
      "Accountability",
    ],
  },
  {
    id: "hr-compliance",
    name: "HR & Compliance",
    programs: [
      "POSH Awareness",
      "Prevention of Workplace Harassment",
      "Diversity & Inclusion",
      "Workplace Behaviour",
    ],
  },
  {
    id: "recruitment",
    name: "Recruitment",
    programs: [
      "Interview Skills",
      "Resume Building",
      "Hiring Managers Workshop",
      "Campus Employability",
    ],
  },
  {
    id: "personal-effectiveness",
    name: "Personal Effectiveness",
    programs: [
      "Confidence Building",
      "Stress Management",
      "Work-Life Balance",
      "Growth Mindset",
      "Personal Branding",
    ],
  },
  {
    id: "fdp-school",
    name: "Faculty Development — School Teachers",
    programs: [
      "Classroom Management",
      "Student Engagement & Motivation",
      "Effective Lesson Planning",
      "Communication Skills for Teachers",
      "Emotional Intelligence in the Classroom",
      "Life Skills Facilitation",
      "Digital Tools & EdTech Integration",
      "Positive Discipline & Behaviour Guidance",
      "POSH for Schools",
      "Parent-Teacher Communication",
    ],
  },
  {
    id: "fdp-college",
    name: "Faculty Development — College Faculty",
    programs: [
      "Pedagogical Innovation & Active Learning",
      "Outcome-Based Education (OBE)",
      "Assessment & Evaluation Techniques",
      "Research & Publication Skills",
      "Executive Presence for Faculty",
      "Mentoring & Student Counselling",
      "Career Guidance & Placement Readiness",
      "Digital Classroom & Blended Learning",
      "POSH for Higher Education",
      "Leadership for HODs & Principals",
    ],
  },
];
const FORMATS = ["In-person", "Virtual", "Hybrid"];
const DURATIONS = ["2 Hours", "Half Day", "Full Day", "Two Days", "Customized"];
const OUTCOMES = [
  "Better customer satisfaction",
  "Higher employee productivity",
  "Improved communication",
  "Better teamwork",
  "Increased sales",
  "Better leadership",
  "Reduced employee conflicts",
  "Improved retention",
  "Higher employee engagement",
  "Better workplace culture",
  "Compliance with POSH",
];
const PRIORITIES = [
  "Immediately",
  "Within 1 Month",
  "Within 3 Months",
  "Within 6 Months",
  "Just Exploring",
];

const SECTIONS = [
  "Company",
  "Challenges",
  "Programs",
  "Format",
  "Outcomes",
  "Priority",
  "Consultation",
];

const emptyForm = {
  company_name: "",
  contact_person: "",
  designation: "",
  mobile_number: "",
  email: "",
  industry: "",
  industry_other: "",
  num_employees: "",
  business_challenges: [],
  business_challenges_other: "",
  interested_programs: [],
  training_format: [],
  preferred_duration: [],
  expected_outcomes: [],
  expected_outcomes_other: "",
  priority_timeline: "",
  consultation_requested: "",
};

export const TnaQuiz = ({ open, onOpenChange }) => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (k, v) =>
    setForm((f) => ({
      ...f,
      [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v],
    }));

  const reset = () => {
    setStep(0);
    setForm(emptyForm);
    setDone(false);
  };

  const closeAndReset = () => {
    onOpenChange(false);
    setTimeout(reset, 200);
  };

  const validCurrentStep = useMemo(() => {
    switch (step) {
      case 0:
        return (
          form.company_name.trim() &&
          form.contact_person.trim() &&
          form.designation.trim() &&
          form.mobile_number.trim().length >= 5 &&
          form.email.trim() &&
          form.industry &&
          (form.industry !== "Other" || form.industry_other.trim()) &&
          form.num_employees
        );
      case 5:
        return !!form.priority_timeline;
      case 6:
        return !!form.consultation_requested;
      default:
        return true;
    }
  }, [step, form]);

  const next = () => {
    if (!validCurrentStep) {
      toast.error("Please complete this section to continue");
      return;
    }
    setStep((s) => Math.min(s + 1, SECTIONS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!validCurrentStep) {
      toast.error("Please answer the final question");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/tna/submit`, form);
      setDone(true);
      toast.success("Thank you — we'll be in touch soon.");
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => (v ? onOpenChange(true) : closeAndReset())}
    >
      <DialogContent
        className="max-w-3xl rounded-none border border-black bg-white p-0 max-h-[92vh] overflow-y-auto"
        data-testid="tna-dialog"
      >
        <div className="bg-[color:var(--bts-yellow)] px-6 py-5 border-b border-black">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-black tracking-tight">
              Business Training Needs Assessment
            </DialogTitle>
            <DialogDescription className="text-black/70 text-sm mt-1">
              {done
                ? "Submitted"
                : `Section ${step + 1} of ${SECTIONS.length} — ${SECTIONS[step]}`}
            </DialogDescription>
          </DialogHeader>
          {!done && (
            <div className="mt-4 h-1 bg-black/20">
              <div
                className="h-full bg-black transition-all"
                style={{ width: `${((step + 1) / SECTIONS.length) * 100}%` }}
              />
            </div>
          )}
        </div>

        {done ? (
          <div className="p-8 space-y-4">
            <div className="w-14 h-14 border border-black flex items-center justify-center bg-[color:var(--bts-yellow)]">
              <Check size={26} />
            </div>
            <h3 className="font-display text-2xl">Assessment received.</h3>
            <p className="text-neutral-700 text-sm">
              Ruchika will personally review your responses
              {form.consultation_requested === "Yes"
                ? " and reach out to schedule your complimentary 30-minute consultation."
                : " and get back to you with a tailored program proposal."}
            </p>
            <button
              className="btn-primary mt-3"
              onClick={closeAndReset}
              data-testid="tna-done-close"
            >
              Close <X size={16} />
            </button>
          </div>
        ) : (
          <div className="p-6 md:p-8 space-y-6">
            {step === 0 && (
              <div className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field
                    label="Company Name *"
                    testid="tna-company"
                    value={form.company_name}
                    onChange={(v) => set("company_name", v)}
                  />
                  <Field
                    label="Contact Person *"
                    testid="tna-contact"
                    value={form.contact_person}
                    onChange={(v) => set("contact_person", v)}
                  />
                  <Field
                    label="Designation *"
                    testid="tna-designation"
                    value={form.designation}
                    onChange={(v) => set("designation", v)}
                  />
                  <Field
                    label="Mobile Number *"
                    testid="tna-mobile"
                    value={form.mobile_number}
                    onChange={(v) => set("mobile_number", v)}
                    placeholder="+91 …"
                  />
                  <Field
                    label="Email Address *"
                    type="email"
                    testid="tna-email"
                    value={form.email}
                    onChange={(v) => set("email", v)}
                  />
                </div>
                <SingleGrid
                  label="Industry *"
                  options={INDUSTRIES}
                  value={form.industry}
                  onChange={(v) => set("industry", v)}
                  testidPrefix="tna-industry"
                />
                {form.industry === "Other" && (
                  <Field
                    label="Please specify industry *"
                    testid="tna-industry-other"
                    value={form.industry_other}
                    onChange={(v) => set("industry_other", v)}
                  />
                )}
                <SingleGrid
                  label="Number of Employees *"
                  options={EMPLOYEE_BUCKETS}
                  value={form.num_employees}
                  onChange={(v) => set("num_employees", v)}
                  testidPrefix="tna-employees"
                  columns={3}
                />
              </div>
            )}

            {step === 1 && (
              <div className="space-y-5">
                <div>
                  <label className="overline block mb-1">
                    Which areas need improvement?
                  </label>
                  <p className="text-xs text-neutral-500 mb-3">
                    Select all that apply.
                  </p>
                  <MultiGrid
                    options={BUSINESS_CHALLENGES}
                    selected={form.business_challenges}
                    onToggle={(v) => toggle("business_challenges", v)}
                    testidPrefix="tna-challenge"
                    columns={2}
                  />
                </div>
                <Field
                  label="Other (please specify)"
                  testid="tna-challenge-other"
                  value={form.business_challenges_other}
                  onChange={(v) => set("business_challenges_other", v)}
                  placeholder="Any challenge not listed above"
                />
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6">
                <p className="text-sm text-neutral-700">
                  Which training programs would you be interested in? Select
                  any across categories.
                </p>
                {PROGRAM_CATEGORIES.map((cat) => (
                  <div key={cat.id}>
                    <div className="font-display text-base mb-2">
                      {cat.name}
                    </div>
                    <MultiGrid
                      options={cat.programs}
                      selected={form.interested_programs}
                      onToggle={(v) => toggle("interested_programs", v)}
                      testidPrefix={`tna-prog-${cat.id}`}
                      columns={2}
                    />
                  </div>
                ))}
              </div>
            )}

            {step === 3 && (
              <div className="space-y-6">
                <div>
                  <label className="overline block mb-3">
                    How would you like the training?
                  </label>
                  <MultiGrid
                    options={FORMATS}
                    selected={form.training_format}
                    onToggle={(v) => toggle("training_format", v)}
                    testidPrefix="tna-format"
                    columns={3}
                  />
                </div>
                <div>
                  <label className="overline block mb-3">
                    Preferred duration
                  </label>
                  <MultiGrid
                    options={DURATIONS}
                    selected={form.preferred_duration}
                    onToggle={(v) => toggle("preferred_duration", v)}
                    testidPrefix="tna-duration"
                    columns={3}
                  />
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-5">
                <div>
                  <label className="overline block mb-1">
                    Expected outcomes
                  </label>
                  <p className="text-xs text-neutral-500 mb-3">
                    What outcomes are most important to your organisation?
                  </p>
                  <MultiGrid
                    options={OUTCOMES}
                    selected={form.expected_outcomes}
                    onToggle={(v) => toggle("expected_outcomes", v)}
                    testidPrefix="tna-outcome"
                    columns={2}
                  />
                </div>
                <Field
                  label="Other outcome (optional)"
                  testid="tna-outcome-other"
                  value={form.expected_outcomes_other}
                  onChange={(v) => set("expected_outcomes_other", v)}
                />
              </div>
            )}

            {step === 5 && (
              <div>
                <label className="overline block mb-3">
                  When are you planning to conduct training? *
                </label>
                <SingleGrid
                  options={PRIORITIES}
                  value={form.priority_timeline}
                  onChange={(v) => set("priority_timeline", v)}
                  testidPrefix="tna-priority"
                  columns={2}
                />
              </div>
            )}

            {step === 6 && (
              <div className="space-y-4">
                <p className="text-base">
                  Would you like a complimentary{" "}
                  <b>30-minute Training Needs Consultation</b> with Ruchika?
                </p>
                <div className="grid grid-cols-2 gap-3 max-w-sm">
                  {["Yes", "No"].map((o) => {
                    const active = form.consultation_requested === o;
                    return (
                      <button
                        key={o}
                        type="button"
                        data-testid={`tna-consult-${o.toLowerCase()}`}
                        onClick={() => set("consultation_requested", o)}
                        className={`border border-black py-3 text-sm font-semibold ${
                          active
                            ? "bg-[color:var(--bts-yellow)]"
                            : "bg-white hover:bg-neutral-50"
                        }`}
                      >
                        {o}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="pt-2 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={back}
                disabled={step === 0}
                data-testid="tna-back"
                className="btn-secondary disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ArrowLeft size={16} /> Back
              </button>
              {step < SECTIONS.length - 1 ? (
                <button
                  type="button"
                  onClick={next}
                  data-testid="tna-next"
                  className="btn-primary"
                >
                  Continue <ArrowUpRight size={16} />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={submit}
                  disabled={submitting}
                  data-testid="tna-submit"
                  className="btn-primary"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Submitting
                    </>
                  ) : (
                    <>
                      Submit assessment <ArrowUpRight size={16} />
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, testid, value, onChange, type = "text", placeholder }) => (
  <div>
    <label className="overline block mb-2">{label}</label>
    <input
      data-testid={testid}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full border border-black bg-white py-2 px-3 focus:outline-none text-sm placeholder:text-neutral-400"
    />
  </div>
);

const idify = (s) => s.replace(/[^a-z0-9]/gi, "-").toLowerCase();

const SingleGrid = ({
  label,
  options,
  value,
  onChange,
  testidPrefix,
  columns = 3,
}) => (
  <div>
    {label && <label className="overline block mb-2">{label}</label>}
    <div
      className={`grid gap-2 ${
        columns === 2
          ? "grid-cols-2"
          : columns === 3
          ? "grid-cols-2 sm:grid-cols-3"
          : "grid-cols-2 sm:grid-cols-4"
      }`}
    >
      {options.map((o) => (
        <button
          key={o}
          type="button"
          data-testid={`${testidPrefix}-${idify(o)}`}
          onClick={() => onChange(o)}
          className={`border border-black py-2 px-3 text-sm text-left ${
            value === o
              ? "bg-[color:var(--bts-yellow)]"
              : "bg-white hover:bg-neutral-50"
          }`}
        >
          {o}
        </button>
      ))}
    </div>
  </div>
);

const MultiGrid = ({ options, selected, onToggle, testidPrefix, columns = 2 }) => (
  <div
    className={`grid gap-2 ${
      columns === 2
        ? "grid-cols-1 sm:grid-cols-2"
        : columns === 3
        ? "grid-cols-2 sm:grid-cols-3"
        : "grid-cols-2"
    }`}
  >
    {options.map((o) => {
      const active = selected.includes(o);
      return (
        <button
          key={o}
          type="button"
          data-testid={`${testidPrefix}-${idify(o)}`}
          onClick={() => onToggle(o)}
          className={`text-left border border-black px-3 py-2 text-sm flex items-center justify-between ${
            active
              ? "bg-[color:var(--bts-yellow)]"
              : "bg-white hover:bg-neutral-50"
          }`}
        >
          <span>{o}</span>
          {active && <Check size={14} />}
        </button>
      );
    })}
  </div>
);

export default TnaQuiz;
